import {
  PartnerTier,
  PartnerTiersResponse,
  PartnerTierResponse,
  PartnerTierStatsResponse,
  CreatePartnerTierRequest,
  UpdatePartnerTierRequest
} from '../types/partnerTier'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class PartnerTierService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all partner tiers
   * @param isActive - Optional filter by active status
   */
  async getTiers(isActive?: boolean): Promise<PartnerTier[]> {
    const url = new URL(`${this.baseURL}/partner-tiers`)

    if (isActive !== undefined) {
      url.searchParams.append('is_active', String(isActive))
    }

    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<PartnerTiersResponse>(response)

    return data.data
  }

  /**
   * Get a single tier by ID
   * @param id - Tier UUID
   */
  async getTier(id: string): Promise<PartnerTier> {
    const url = `${this.baseURL}/partner-tiers/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerTierResponse>(response)

    return data.data
  }

  /**
   * Create a new partner tier
   * @param tierData - Tier data
   */
  async createTier(tierData: CreatePartnerTierRequest): Promise<PartnerTier> {
    const url = `${this.baseURL}/partner-tiers`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(tierData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerTierResponse>(response)

    return data.data
  }

  /**
   * Update an existing partner tier
   * @param id - Tier UUID
   * @param updates - Partial tier data to update
   */
  async updateTier(id: string, updates: UpdatePartnerTierRequest): Promise<PartnerTier> {
    const url = `${this.baseURL}/partner-tiers/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerTierResponse>(response)

    return data.data
  }

  /**
   * Get tier statistics (partner count)
   * @param id - Tier UUID
   */
  async getTierStats(id: string): Promise<{ tier: PartnerTier; stats: { total_partners: number } }> {
    const url = `${this.baseURL}/partner-tiers/${id}/stats`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerTierStatsResponse>(response)

    return data.data
  }
}

export const partnerTierService = new PartnerTierService()
